import { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getUserPermissions,
  DEFAULT_ROLE_PERMISSIONS,
} from '../utils/permissions';

/**
 * React hook for checking user permissions
 * 
 * Combines role-based permissions with user-specific overrides (grants/denials).
 * Listens to rolePermissions collection for real-time updates.
 * 
 * @param {Object} user - Current user object with role and uid
 * @returns {Object} Permission checking functions and loading state
 */
export function usePermissions(user) {
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Subscribe to rolePermissions collection for real-time updates
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      collection(db, collectionPath('rolePermissions')),
      (snapshot) => {
        const perms = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          perms[doc.id] = data.permissions || [];
        });
        
        // Merge with defaults for any missing roles
        Object.keys(DEFAULT_ROLE_PERMISSIONS).forEach(role => {
          if (!perms[role]) {
            perms[role] = DEFAULT_ROLE_PERMISSIONS[role];
          }
        });
        
        setRolePermissions(perms);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching role permissions:', error);
        // Fallback to defaults on error
        setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  // Memoize permission checking functions
  const can = useMemo(() => {
    return (permission) => hasPermission(user, permission, rolePermissions);
  }, [user, rolePermissions]);

  const canAny = useMemo(() => {
    return (permissions) => hasAnyPermission(user, permissions, rolePermissions);
  }, [user, rolePermissions]);

  const canAll = useMemo(() => {
    return (permissions) => hasAllPermissions(user, permissions, rolePermissions);
  }, [user, rolePermissions]);

  const permissions = useMemo(() => {
    return getUserPermissions(user, rolePermissions);
  }, [user, rolePermissions]);

  return {
    can,           // Check single permission
    canAny,        // Check if has any of the permissions
    canAll,        // Check if has all permissions
    permissions,   // Array of all effective permissions
    loading,       // Loading state
    rolePermissions, // Raw role permissions object
  };
}

/**
 * Hook for fetching role permissions (for admin pages)
 * @returns {Object} Role permissions and CRUD functions
 */
export function useRolePermissions() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, collectionPath('rolePermissions')),
      (snapshot) => {
        const rolesList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.id,
          permissions: doc.data().permissions || [],
          description: doc.data().description || '',
          isCustom: doc.data().isCustom || false,
        }));
        setRoles(rolesList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching roles:', error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  return {
    roles,
    loading,
  };
}
